import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Divider,
  Tooltip,
  Skeleton,
} from '@mui/material';
import {
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  ShowChart as ShowChartIcon,
  TrendingUp as TrendingUpIcon,
  SwapHoriz as SwapHorizIcon,
  Search as SearchIcon,
  VerifiedUser as VerifiedUserIcon,
  AddCircle as AddCircleIcon,
  HourglassEmpty as WaitIcon,
  Autorenew as AutorenewIcon,
} from '@mui/icons-material';
import { XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Area, AreaChart, ReferenceLine } from 'recharts';
import { marketCorrectionAPI } from '../services/api';

const MarketCorrectionPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [currentData, setCurrentData] = useState(null);
  const [mlData, setMlData] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [entrySignal, setEntrySignal] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [currentRes, historyRes, signalRes, mlRes] = await Promise.all([
        marketCorrectionAPI.getCurrent(),
        marketCorrectionAPI.getHistory(30),
        marketCorrectionAPI.getEntrySignal(),
        marketCorrectionAPI.getMultiHorizonLatest().catch(() => ({ data: { success: false } })),
      ]);

      if (currentRes.data.success) {
        setCurrentData(currentRes.data);
      }
      
      if (historyRes.data.success) {
        setHistoryData(historyRes.data.history);
      }
      
      if (signalRes.data.success) {
        setEntrySignal(signalRes.data);
      }

      if (mlRes.data?.success) {
        setMlData(mlRes.data);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch market correction data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        {/* Header skeleton — matches new gradient icon box style */}
        <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="rounded" width={42} height={42} animation="wave" sx={{ borderRadius: 2.5, flexShrink: 0 }} />
            <Box>
              <Skeleton width={220} height={22} animation="wave" />
              <Skeleton width={320} height={14} animation="wave" sx={{ mt: 0.3 }} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Skeleton variant="rounded" width={90} height={34} animation="wave" sx={{ borderRadius: 1 }} />
            <Skeleton variant="rounded" width={110} height={34} animation="wave" sx={{ borderRadius: 1 }} />
          </Box>
        </Box>

        {/* ── 4 Stat Cards ── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'stretch' }}>
          {[0, 1, 2, 3].map(i => (
            <Box key={i} sx={{ flex: '1 1 0', minWidth: 220, display: 'flex' }}>
              <Card sx={{ width: '100%', minHeight: 210, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden', position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3 }}>
                  <Skeleton variant="rectangular" height={3} animation="wave" />
                </Box>
                <CardContent sx={{ p: 2, pt: 2.2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Skeleton width={100} height={12} animation="wave" />
                    <Skeleton variant="rounded" width={24} height={16} animation="wave" sx={{ borderRadius: 1 }} />
                  </Box>
                  {i === 0 && <>
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                      <Skeleton variant="circular" width={96} height={48} animation="wave" sx={{ borderRadius: '96px 96px 0 0' }} />
                    </Box>
                    <Skeleton width={80} height={18} animation="wave" sx={{ mx: 'auto', mb: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.8 }}>
                      <Skeleton variant="rounded" width={55} height={16} animation="wave" sx={{ borderRadius: 1 }} />
                      <Skeleton variant="rounded" width={55} height={16} animation="wave" sx={{ borderRadius: 1 }} />
                    </Box>
                  </>}
                  {i === 1 && <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Skeleton variant="rounded" width={36} height={36} animation="wave" sx={{ borderRadius: '10px' }} />
                      <Box>
                        <Skeleton width={110} height={24} animation="wave" />
                        <Skeleton width={70} height={10} animation="wave" sx={{ mt: 0.3 }} />
                      </Box>
                    </Box>
                    <Skeleton width="90%" height={12} animation="wave" sx={{ mt: 1 }} />
                    <Skeleton width="70%" height={12} animation="wave" sx={{ mt: 0.3 }} />
                  </>}
                  {i === 2 && <>
                    <Skeleton width={120} height={14} animation="wave" sx={{ mb: 1 }} />
                    {[0, 1, 2, 3].map(j => (
                      <Box key={j} sx={{ mb: 0.8 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                          <Skeleton width={60} height={10} animation="wave" />
                          <Skeleton width={40} height={14} animation="wave" />
                        </Box>
                        <Skeleton variant="rounded" height={5} animation="wave" sx={{ borderRadius: 3 }} />
                      </Box>
                    ))}
                  </>}
                  {i === 3 && <>
                    <Skeleton width={70} height={32} animation="wave" sx={{ mt: 0.3 }} />
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 0.8 }}>
                      <Box><Skeleton width={60} height={10} animation="wave" /><Skeleton width={45} height={16} animation="wave" /></Box>
                      <Box><Skeleton width={60} height={10} animation="wave" /><Skeleton width={45} height={16} animation="wave" /></Box>
                    </Box>
                    <Skeleton variant="rounded" height={6} animation="wave" sx={{ mt: 1, borderRadius: 3 }} />
                  </>}
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>

        {/* ── Actions bar skeleton ── */}
        <Skeleton variant="rounded" height={56} animation="wave" sx={{ borderRadius: 2, mb: 3 }} />

        {/* ── Entry Signal skeleton ── */}
        <Card sx={{ mb: 3, borderRadius: 2.5, overflow: 'hidden' }}>
          <Skeleton variant="rectangular" height={72} animation="wave" />
          <Box sx={{ display: 'flex', bgcolor: '#fafafa' }}>
            {[0, 1, 2].map(j => (
              <Box key={j} sx={{ flex: 1, p: 1.5, textAlign: 'center', borderRight: j < 2 ? '1px solid rgba(0,0,0,0.06)' : 'none' }}>
                <Skeleton width={50} height={10} animation="wave" sx={{ mx: 'auto' }} />
                <Skeleton width={40} height={26} animation="wave" sx={{ mx: 'auto', mt: 0.3 }} />
                <Skeleton variant="rounded" width="80%" height={4} animation="wave" sx={{ mx: 'auto', mt: 0.5, borderRadius: 2 }} />
              </Box>
            ))}
          </Box>
          <Box sx={{ px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="rounded" width={16} height={16} animation="wave" />
            <Skeleton width="60%" height={12} animation="wave" />
          </Box>
        </Card>

        {/* ── Factors + Market Health Row skeleton ── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'stretch' }}>
          {/* Factors */}
          <Box sx={{ flex: '1 1 0', minWidth: 400, display: 'flex' }}>
            <Card sx={{ width: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderBottom: '2px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.02)' }}>
                  <Skeleton width={130} height={16} animation="wave" />
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Skeleton variant="rounded" width={50} height={16} animation="wave" sx={{ borderRadius: 1 }} />
                    <Skeleton variant="rounded" width={32} height={20} animation="wave" sx={{ borderRadius: 3 }} />
                  </Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                  {[0, 1].map(col => (
                    <Box key={col} sx={{ borderRight: col === 0 ? '1px solid rgba(0,0,0,0.06)' : 'none' }}>
                      {[0, 1, 2, 3].map(row => (
                        <Box key={row} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderBottom: row < 3 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
                          <Skeleton variant="circular" width={6} height={6} animation="wave" sx={{ flexShrink: 0 }} />
                          <Box sx={{ flex: 1 }}>
                            <Skeleton width="70%" height={12} animation="wave" />
                            <Skeleton width="50%" height={10} animation="wave" sx={{ mt: 0.2 }} />
                          </Box>
                          <Skeleton variant="rounded" width={40} height={18} animation="wave" sx={{ borderRadius: 1 }} />
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>
          {/* Market Health */}
          <Box sx={{ flex: '1 1 0', minWidth: 350, display: 'flex' }}>
            <Card sx={{ width: '100%', border: '1px solid rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderBottom: '2px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.02)' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Skeleton variant="rounded" width={20} height={20} animation="wave" />
                    <Skeleton width={120} height={16} animation="wave" />
                  </Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.2fr 2.5fr 0.5fr', gap: 1, px: 2, py: 1, borderBottom: '1px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.03)' }}>
                  {[80, 40, 50, 100, 25].map((w, k) => <Skeleton key={k} width={w} height={10} animation="wave" />)}
                </Box>
                {[0, 1, 2].map(row => (
                  <Box key={row} sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.2fr 2.5fr 0.5fr', gap: 1, px: 2, py: 1.25, borderBottom: row < 2 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
                    <Skeleton width="65%" height={14} animation="wave" />
                    <Skeleton width={45} height={16} animation="wave" />
                    <Skeleton width={50} height={12} animation="wave" />
                    <Skeleton width="75%" height={10} animation="wave" />
                    <Skeleton variant="rounded" width={18} height={18} animation="wave" sx={{ justifySelf: 'center' }} />
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* ── Charts Row skeleton ── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <Box sx={{ flex: '2 1 0', minWidth: 400 }}>
            <Card sx={{ height: 400 }}>
              <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Skeleton width={160} height={20} animation="wave" sx={{ mb: 1.5 }} />
                <Skeleton variant="rounded" animation="wave" sx={{ flex: 1, borderRadius: 1.5 }} />
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: '1 1 0', minWidth: 300 }}>
            <Card sx={{ height: 400 }}>
              <CardContent>
                <Skeleton width={120} height={20} animation="wave" sx={{ mb: 2 }} />
                {[0, 1, 2].map(k => (
                  <Box key={k} sx={{ mb: 2.5 }}>
                    <Skeleton width={70} height={12} animation="wave" sx={{ mb: 0.5 }} />
                    <Skeleton width={90} height={28} animation="wave" />
                  </Box>
                ))}
                <Skeleton variant="rounded" height={44} animation="wave" sx={{ mt: 2, borderRadius: 1 }} />
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 2.5, flexShrink: 0,
            background: 'linear-gradient(135deg, #ef6c00 0%, #ff9800 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 3px 10px rgba(239,108,0,0.3)',
          }}>
            <ShowChartIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>
                {t('marketCorrection.title')}
              </Typography>
              {currentData?.date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef6c00', boxShadow: '0 0 6px #ef6c00' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                    {currentData.date}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.3 }}>
              {t('marketCorrection.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="outlined" size="small" disableElevation
            startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon sx={{ fontSize: 15 }} />}
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1, height: 34, px: 2, fontSize: '0.78rem' }}
          >
            {refreshing ? t('marketCorrection.refreshing') : t('marketCorrection.refresh')}
          </Button>
          <Button
            variant="contained" size="small" disableElevation
            startIcon={refreshing ? <CircularProgress size={14} color="inherit" /> : <AutorenewIcon sx={{ fontSize: 15 }} />}
            onClick={async () => {
              setRefreshing(true);
              try {
                await marketCorrectionAPI.recalculate();
                await fetchData();
              } catch (err) {
                setError('Failed to recalculate');
              } finally {
                setRefreshing(false);
              }
            }}
            disabled={refreshing}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1, height: 34, px: 2, fontSize: '0.78rem', bgcolor: '#f57c00', '&:hover': { bgcolor: '#ef6c00' } }}
          >
            {t('marketCorrection.recalculate', { defaultValue: 'Recalculate' })}
          </Button>
        </Box>
      </Box>

      {/* Main Stats Row — equal height via stretch */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <Box sx={{ flex: '1 1 0', minWidth: 220, display: 'flex' }}>
          <RiskScoreCard data={currentData} />
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: 220, display: 'flex' }}>
          <RegimeCard data={currentData} />
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: 220, display: 'flex' }}>
          <MLProbabilityCard mlData={mlData} fallbackData={currentData} />
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: 220, display: 'flex' }}>
          <DrawdownCard data={currentData} />
        </Box>
      </Box>

      {/* Actions recommendation bar */}
      <Box sx={{ mb: 3 }}>
        <ActionsCard data={currentData} />
      </Box>

      {/* Entry Signal Card - Most actionable info first */}
      {entrySignal && (
        <Box sx={{ mb: 3 }}>
          <EntrySignalCard signal={entrySignal} />
        </Box>
      )}

      {/* Factors + Market Health Row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <Box sx={{ flex: '1 1 0', minWidth: 400, display: 'flex' }}>
          <FactorsBreakdownCard data={currentData} />
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: 350, display: 'flex' }}>
          <MarketHealthCard data={currentData} />
        </Box>
      </Box>

      {/* Charts Row */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '2 1 0', minWidth: 400 }}>
          <HistoryChart data={historyData} mlData={mlData} currentData={currentData} />
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: 300 }}>
          <SupportLevelsCard data={currentData} />
        </Box>
      </Box>
    </Box>
  );
};

// Unified Stat Card style helper
const STAT_CARD_MIN_HEIGHT = 210;
const StatCard = ({ children, accentColor = '#1a237e' }) => (
  <Card sx={{ height: '100%', width: '100%', minHeight: STAT_CARD_MIN_HEIGHT, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: 'none', overflow: 'hidden', position: 'relative', display: 'flex', flexDirection: 'column' }}>
    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: accentColor }} />
    <CardContent sx={{ p: 2, pt: 2.2, '&:last-child': { pb: 2 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
      {children}
    </CardContent>
  </Card>
);

// Risk Score Card
// Helper: compute composite risk score from rule-based + ML.
// Backend provides a display-ready ML score on the same 0-100 axis used
// by the gauge, so the UI does not have to rescale excess-over-base data.
const RISK_BAND_COLORS = {
  very_dangerous: '#d32f2f',
  dangerous: '#f57c00',
  caution: '#ffa000',
  safe: '#388e3c',
};

const DRAWDOWN_BAND_COLORS = {
  very_deep: '#c62828',
  quite_deep: '#d32f2f',
  moderate: '#f57c00',
  light: '#ffa000',
};

const CONTEXT_TONE_COLORS = {
  danger: '#d32f2f',
  warning: '#e65100',
  positive: '#388e3c',
  neutral: 'text.secondary',
};

const FACTOR_STATUS_STYLES = {
  dangerous: { color: '#991b1b', bg: '#fee2e2', chip: '#991b1b' },
  warning: { color: '#c62828', bg: '#ffebee', chip: '#c62828' },
  notice: { color: '#e65100', bg: '#fff3e0', chip: '#e65100' },
  safe: { color: '#2e7d32', bg: '#e8f5e9', chip: '#2e7d32' },
};

const HEALTH_STATUS_COLORS = {
  high: '#4caf50',
  moderate: '#ff9800',
  low: '#9e9e9e',
  severe: '#f44336',
  mild: '#4caf50',
};

const trendIconForKey = (trend) => {
  if (trend === 'up') return <TrendingUpIcon />;
  if (trend === 'flat') return <SwapHorizIcon />;
  return <TrendingDownIcon />;
};

const trendColorForKey = (trend) => {
  if (trend === 'up') return '#4caf50';
  if (trend === 'flat') return '#ff9800';
  return '#f44336';
};

const RiskScoreCard = ({ data }) => {
  const { t } = useTranslation();
  if (!data) return null;
  const summaryView = data.summary_view || {};
  const score = Number(summaryView.score ?? data.risk_score ?? 0);
  const ruleScore = Number(summaryView.rule_score ?? data.risk_score ?? 0);
  const mlScore = summaryView.ml_score != null ? Number(summaryView.ml_score) : null;
  const source = summaryView.source || 'rule';
  const band = summaryView.band || 'safe';
  const color = RISK_BAND_COLORS[band] || RISK_BAND_COLORS.safe;
  const labelKey = {
    very_dangerous: 'marketCorrection.riskLabel.veryDangerous',
    dangerous: 'marketCorrection.riskLabel.dangerous',
    caution: 'marketCorrection.riskLabel.caution',
    safe: 'marketCorrection.riskLabel.safe',
  }[band] || 'marketCorrection.riskLabel.safe';
  const label = t(labelKey);
  const advice = t(summaryView.advice_key || 'marketCorrection.riskAdvice.low');
  const contextMetrics = data.market_view?.context_metrics || [];

  // SVG semicircular gauge
  const radius = 48;
  const stroke = 8;
  const cx = 70, cy = 56;
  const startAngle = Math.PI;
  const endAngle = 0;
  const arcLength = Math.PI;
  const progress = Math.min(score / 100, 1);

  const describeArc = (r, start, end) => {
    const x1 = cx + r * Math.cos(start);
    const y1 = cy - r * Math.sin(start);
    const x2 = cx + r * Math.cos(end);
    const y2 = cy - r * Math.sin(end);
    return `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`;
  };

  const bgPath = describeArc(radius, startAngle, endAngle);
  const fillEnd = startAngle - progress * arcLength;
  const fillPath = describeArc(radius, startAngle, fillEnd);

  const zoneColors = ['#388e3c', '#ffa000', '#f57c00', '#d32f2f'];
  const zonePcts = [0.30, 0.50, 0.70];

  return (
    <StatCard accentColor={color}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {t('marketCorrection.riskScore.title')}
        </Typography>
        {source === 'ml' && (
          <Tooltip title={t('marketCorrection.riskTooltip.aiChip')} arrow placement="top">
            <Chip label="AI" size="small" sx={{ height: 16, fontSize: '0.5rem', fontWeight: 800, bgcolor: '#1565c0', color: '#fff', '& .MuiChip-label': { px: 0.4 } }} />
          </Tooltip>
        )}
      </Box>

      {/* Gauge */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
        <svg width="140" height="78" viewBox="0 0 140 78">
          {/* Background arc */}
          <path d={bgPath} fill="none" stroke="#eee" strokeWidth={stroke} strokeLinecap="round" />
          {/* Zone color segments */}
          {[0, 0.30, 0.50, 0.70].map((start, i) => {
            const segEnd = i < 3 ? zonePcts[i] : 1;
            const a1 = startAngle - start * arcLength;
            const a2 = startAngle - segEnd * arcLength;
            return <path key={i} d={describeArc(radius, a1, a2)} fill="none" stroke={`${zoneColors[i]}30`} strokeWidth={stroke} />;
          })}
          {/* Active fill */}
          <path d={fillPath} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
            style={{ transition: 'all 0.6s ease' }} />
          {/* Score text */}
          <text x={cx} y={cy - 8} textAnchor="middle" fontSize="24" fontWeight="900" fill={color}>{score.toFixed(0)}</text>
          <text x={cx} y={cy + 5} textAnchor="middle" fontSize="9" fontWeight="600" fill="#999">/100</text>
          {/* Zone labels below arc ends */}
          <text x={cx - radius} y={cy + 18} textAnchor="middle" fontSize="8.5" fill="#388e3c" fontWeight="700">{t('marketCorrection.gaugeLabel.safe')}</text>
          <text x={cx + radius} y={cy + 18} textAnchor="middle" fontSize="8.5" fill="#d32f2f" fontWeight="700">{t('marketCorrection.gaugeLabel.dangerous')}</text>
        </svg>
      </Box>

      {/* Label */}
      <Box sx={{ textAlign: 'center', mt: 0 }}>
        <Typography sx={{ fontSize: '1rem', fontWeight: 900, color, letterSpacing: '-0.01em' }}>{label}</Typography>
      </Box>

      {/* Source pills */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.8, mt: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, bgcolor: '#f5f5f5', borderRadius: 1, px: 0.6, py: 0.15 }}>
          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#9e9e9e' }} />
          <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', fontWeight: 600 }}>Rule: {ruleScore.toFixed(0)}</Typography>
        </Box>
        {mlScore > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, bgcolor: '#e3f2fd', borderRadius: 1, px: 0.6, py: 0.15 }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#1565c0' }} />
            <Typography sx={{ fontSize: '0.55rem', color: '#1565c0', fontWeight: 700 }}>ML: {mlScore.toFixed(0)}</Typography>
          </Box>
        )}
      </Box>

      {/* Compact context: severity + source bar */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.6, mt: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.45rem', color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase' }}>{t('marketCorrection.contextLabel.severity')}</Typography>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: color }}>{data.market_view?.severity || data.severity || 'Low'}</Typography>
          </Box>
          <Box sx={{ width: '1px', bgcolor: 'rgba(0,0,0,0.08)' }} />
          {(contextMetrics[0] || contextMetrics[3]) && (
            <>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.45rem', color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase' }}>{t(`marketCorrection.contextLabel.${contextMetrics[0]?.code || 'dd20d'}`)}</Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: CONTEXT_TONE_COLORS[contextMetrics[0]?.tone] || 'text.secondary' }}>{contextMetrics[0]?.display_value}</Typography>
              </Box>
              <Box sx={{ width: '1px', bgcolor: 'rgba(0,0,0,0.08)' }} />
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: '0.45rem', color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase' }}>{t('marketCorrection.contextLabel.source')}</Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: source === 'ml' ? '#1565c0' : '#757575' }}>{source === 'ml' ? 'ML' : 'Rule'}</Typography>
              </Box>
            </>
          )}
        </Box>
        {mlScore != null && (
          <Box sx={{ mx: 1 }}>
            <Box sx={{ display: 'flex', height: 5, borderRadius: 3, overflow: 'hidden', bgcolor: '#eee' }}>
              <Box sx={{ width: `${Math.min((ruleScore / Math.max(score, 1)) * 100, 100)}%`, bgcolor: '#bdbdbd', transition: 'width 0.5s' }} />
              <Box sx={{ flex: 1, bgcolor: '#42a5f5', transition: 'width 0.5s' }} />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.15 }}>
              <Typography sx={{ fontSize: '0.42rem', color: '#9e9e9e', fontWeight: 600 }}>Rule {ruleScore.toFixed(0)}%</Typography>
              <Typography sx={{ fontSize: '0.42rem', color: '#1565c0', fontWeight: 600 }}>ML {mlScore.toFixed(0)}%</Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Advice */}
      <Box sx={{ bgcolor: `${color}10`, borderRadius: 1, px: 0.8, py: 0.4, mt: 'auto' }}>
        <Typography sx={{ fontSize: '0.58rem', color, fontWeight: 600, lineHeight: 1.3 }}>
          💡 {advice}
        </Typography>
      </Box>
    </StatCard>
  );
};

// Regime Card
const RegimeCard = ({ data }) => {
  const { t } = useTranslation();
  if (!data) return null;
  const marketView = data.market_view || {};
  const regime = (marketView.regime || data.regime || 'Unknown').trim();

  // Keep regime authority in the backend response. ML disagreement is surfaced
  // as a warning chip, but it should not silently flip the displayed regime.
  const summaryView = data.summary_view || {};

  const cfgs = {
    'risk-on': { color: '#2e7d32', label: t('marketCorrection.regime.riskOn'), desc: t('marketCorrection.regime.riskOnDesc', { defaultValue: 'Thị trường thuận lợi, có thể đầu tư bình thường' }), icon: <TrendingUpIcon />,
      tips: t('marketCorrection.regimeTips.riskOn', { returnObjects: true }) },
    'caution': { color: '#e65100', label: t('marketCorrection.regime.caution'), desc: t('marketCorrection.regimeDetail.cautionDesc'), icon: <WarningIcon />,
      tips: t('marketCorrection.regimeTips.caution', { returnObjects: true }) },
    'risk-off': { color: '#c62828', label: t('marketCorrection.regime.riskOff'), desc: t('marketCorrection.regime.riskOffDesc', { defaultValue: 'Rủi ro cao, ưu tiên bảo toàn vốn' }), icon: <TrendingDownIcon />,
      tips: t('marketCorrection.regimeTips.riskOff', { returnObjects: true }) },
  };
  const cfg = cfgs[regime.toLowerCase()] || { color: '#757575', label: regime, desc: '', icon: <InfoIcon />, tips: [] };
  const severity = marketView.severity || data.severity || 'Low';
  const mlWarning = Boolean(marketView.ml_warning);
  const mlWarningScore = marketView.ml_score ?? summaryView.score ?? data.risk_score ?? 0;
  const recoverySignal = data.recovery_signal || 0;
  const contextMetrics = marketView.context_metrics || [];

  return (
    <StatCard accentColor={cfg.color}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {t('marketCorrection.regime.title')}
        </Typography>
        {mlWarning && (
          <Tooltip title={t('marketCorrection.riskTooltip.regimeUpgrade')} arrow placement="top">
            <Chip label="AI" size="small" sx={{ height: 16, fontSize: '0.5rem', fontWeight: 800, bgcolor: '#e65100', color: '#fff', '& .MuiChip-label': { px: 0.4 } }} />
          </Tooltip>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.8 }}>
        {mlWarning && (
          <Box sx={{ mt: 0.8, px: 1, py: 0.75, borderRadius: 1.5, bgcolor: '#fff3e0', border: '1px solid #ffe0b2' }}>
            <Typography sx={{ fontSize: '0.6rem', color: '#e65100', fontWeight: 700, lineHeight: 1.35 }}>
              {t('marketCorrection.regime.mlWarningTitle', { defaultValue: 'AI cảnh báo nhưng chưa đảo chế độ thị trường' })}
            </Typography>
            <Typography sx={{ fontSize: '0.58rem', color: '#bf360c', mt: 0.25, lineHeight: 1.4 }}>
              {t('marketCorrection.regime.mlWarningBody', {
                defaultValue: 'ML đang thấy xác suất correction khoảng {{score}}/100, nhưng cấu trúc hiện tại vẫn chưa xác nhận Risk-off.',
                score: Number(mlWarningScore || 0).toFixed(0),
              })}
            </Typography>
          </Box>
        )}
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${cfg.color}12` }}>
          {React.cloneElement(cfg.icon, { sx: { fontSize: '1.3rem', color: cfg.color } })}
        </Box>
        <Box>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 900, color: cfg.color, lineHeight: 1 }}>{cfg.label}</Typography>
          <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', mt: 0.2 }}>{t('marketCorrection.regimeDetail.severityLabel')}: {severity}</Typography>
        </Box>
      </Box>
      <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', mt: 0.6 }}>{cfg.desc}</Typography>

      {/* Compact context strip */}
      <Box sx={{ mt: 1, py: 0.8, display: 'flex', justifyContent: 'space-around' }}>
        {contextMetrics.map((metric) => (
          <Box key={metric.code} sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.45rem', color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase' }}>{t(`marketCorrection.contextLabel.${metric.code}`)}</Typography>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: CONTEXT_TONE_COLORS[metric.tone] || 'text.secondary' }}>{metric.display_value}</Typography>
          </Box>
        ))}
      </Box>

      {/* Tips list — pushed to bottom */}
      <Box sx={{ mt: 'auto', pt: 0.8 }}>
        <Box sx={{ px: 1, py: 0.6, borderRadius: 1.5, bgcolor: `${cfg.color}06`, border: `1px solid ${cfg.color}12` }}>
          <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: cfg.color, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.3 }}>
            {t('marketCorrection.regime.actionLabel', { defaultValue: 'Hành động' })}
          </Typography>
          {(cfg.tips || []).map((tip, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.15 }}>
              <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: cfg.color, opacity: 0.5, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.6rem', color: cfg.color, fontWeight: 500 }}>{tip}</Typography>
            </Box>
          ))}
        </Box>
        {recoverySignal > 0.5 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, px: 0.5 }}>
            <AutorenewIcon sx={{ fontSize: '0.7rem', color: '#4caf50' }} />
            <Typography sx={{ fontSize: '0.55rem', color: '#4caf50', fontWeight: 700 }}>{t('marketCorrection.regimeDetail.recoverySignal')}</Typography>
          </Box>
        )}
      </Box>
    </StatCard>
  );
};

// Map backend risk_level string → UI color. Keep this the only place
// where severity → color mapping lives on the frontend.
const RISK_LEVEL_COLORS = {
  very_high: '#d32f2f',
  high: '#f57c00',
  medium: '#ffa000',
  low: '#388e3c',
  unknown: '#9e9e9e',
};
const RISK_LEVEL_DESC_KEYS = {
  very_high: 'marketCorrection.mlProb.aboveAvg',
  high: 'marketCorrection.mlProb.slightlyHigh',
  medium: 'marketCorrection.mlProb.average',
  low: 'marketCorrection.mlProb.belowAvg',
};

// ML Probability Card — uses real ML model predictions
const MLProbabilityCard = ({ mlData, fallbackData }) => {
  const { t } = useTranslation();
  const HORIZON_LABELS = { 5: t('marketCorrection.horizon.h5'), 10: t('marketCorrection.horizon.h10'), 20: t('marketCorrection.horizon.h20'), 30: t('marketCorrection.horizon.h30') };

  const horizons = mlData?.horizons;
  const hasML = horizons && Object.keys(horizons).length > 0;

  // Fallback to rule-based if ML unavailable
  if (!hasML) {
    const prob5d = fallbackData?.probabilities?.p_pullback_5d_adj != null ? fallbackData.probabilities.p_pullback_5d_adj * 100 : (fallbackData?.probabilities?.p_pullback_5d || 0) * 100;
    const prob10d = fallbackData?.probabilities?.p_pullback_10d_adj != null ? fallbackData.probabilities.p_pullback_10d_adj * 100 : (fallbackData?.probabilities?.p_pullback_10d || 0) * 100;
    const mainProb = Math.max(prob5d, prob10d);
    const mainColor = mainProb > 50 ? '#d32f2f' : mainProb > 30 ? '#f57c00' : '#388e3c';
    return (
      <StatCard accentColor={mainColor}>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('marketCorrection.mlProb.titleFallback')}</Typography>
        <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, color: mainColor, mt: 0.5 }}>{mainProb.toFixed(0)}%</Typography>
        <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', mt: 0.3 }}>{t('marketCorrection.mlProb.fallbackNote')}</Typography>
      </StatCard>
    );
  }

  const entries = Object.entries(horizons)
    .filter(([, h]) => h.available)
    .sort(([a], [b]) => Number(a) - Number(b));
  // Overall severity comes from backend (risk_assessment.assess_multi_horizon).
  // Frontend only maps the level string → color/label.
  const overall = mlData?.overall_risk || {};
  const overallLevel = overall.risk_level || 'low';
  const mainColor = RISK_LEVEL_COLORS[overallLevel] || RISK_LEVEL_COLORS.low;
  const mainLabel = t(overall.risk_label_key || 'marketCorrection.mlProb.riskLow');

  return (
    <StatCard accentColor={mainColor}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('marketCorrection.mlProb.title')}</Typography>
        <Tooltip title={t('marketCorrection.mlProb.tooltip')} arrow placement="top">
          <Chip label="AI" size="small" sx={{ height: 16, fontSize: '0.5rem', fontWeight: 800, bgcolor: '#1565c0', color: '#fff', '& .MuiChip-label': { px: 0.4 } }} />
        </Tooltip>
      </Box>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: mainColor, mt: 0.3, mb: 0.6 }}>{mainLabel}</Typography>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      {entries.map(([h, hData], idx) => {
        const pCorr = (hData.p_corr || 0) * 100;
        // Backend-provided: base_rate (fraction), excess_pp, risk_level, risk_label_key.
        const baseRate = (hData.base_rate != null ? hData.base_rate : 0.5) * 100;
        const excessPp = hData.excess_pp != null ? hData.excess_pp : (pCorr - baseRate);
        const level = hData.risk_level || 'low';
        const c = RISK_LEVEL_COLORS[level] || RISK_LEVEL_COLORS.low;
        const desc = t(RISK_LEVEL_DESC_KEYS[level] || RISK_LEVEL_DESC_KEYS.low);
        const excessLabel = (excessPp >= 0 ? '+' : '') + excessPp.toFixed(0) + 'pp';
        return (
          <Box key={h} sx={{ py: 0.4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.2 }}>
              <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>{HORIZON_LABELS[Number(h)] || `${h}d`}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, color: c, lineHeight: 1 }}>{pCorr.toFixed(0)}%</Typography>
                <Typography sx={{ fontSize: '0.55rem', color: c, fontWeight: 700 }}>({excessLabel})</Typography>
                <Chip label={desc} size="small" sx={{ height: 14, fontSize: '0.45rem', fontWeight: 700, bgcolor: `${c}12`, color: c, '& .MuiChip-label': { px: 0.3 } }} />
              </Box>
            </Box>
            <Box sx={{ position: 'relative', height: 5, borderRadius: 3, bgcolor: '#f0f0f0', overflow: 'hidden' }}>
              <Box sx={{ width: `${Math.min(pCorr, 100)}%`, height: '100%', borderRadius: 3, bgcolor: c, transition: 'width 0.5s' }} />
              {/* Base rate marker */}
              <Box sx={{ position: 'absolute', left: `${Math.min(baseRate, 100)}%`, top: -1, width: 1.5, height: 7, bgcolor: '#999', borderRadius: 1 }} />
            </Box>
          </Box>
        );
      })}
      </Box>
      {/* Summary box — fills remaining space at bottom */}
      <Box sx={{ mt: 'auto', pt: 0.6, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
        {(() => {
          const avgPCorr = entries.reduce((s, [, h]) => s + (h.p_corr || 0), 0) / entries.length * 100;
          const avgBase = entries.reduce((s, [, h]) => s + (h.base_rate != null ? h.base_rate : 0.5), 0) / entries.length * 100;
          const maxEntry = entries.reduce((max, cur) => (cur[1].p_corr || 0) > (max[1].p_corr || 0) ? cur : max, entries[0]);
          const maxH = HORIZON_LABELS[Number(maxEntry[0])] || maxEntry[0];
          return (
            <Box sx={{ px: 1, py: 0.6, borderRadius: 1.5, bgcolor: `${mainColor}06`, border: `1px solid ${mainColor}12` }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: mainColor, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t('marketCorrection.mlProb.overview')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                  <Box sx={{ width: 8, height: 1.5, bgcolor: '#999', borderRadius: 1 }} />
                  <Typography sx={{ fontSize: '0.45rem', color: 'text.disabled' }}>{t('marketCorrection.mlProb.baseRate')}</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Box>
                  <Typography sx={{ fontSize: '0.48rem', color: 'text.disabled', fontWeight: 600 }}>{t('marketCorrection.mlProb.avgProb')}</Typography>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: mainColor }}>{avgPCorr.toFixed(0)}% <Typography component="span" sx={{ fontSize: '0.5rem', color: 'text.disabled', fontWeight: 500 }}>{t('marketCorrection.mlProb.vsAvg', { avg: avgBase.toFixed(0) })}</Typography></Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.48rem', color: 'text.disabled', fontWeight: 600 }}>{t('marketCorrection.mlProb.highest')}</Typography>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: mainColor }}>{maxH}</Typography>
                </Box>
              </Box>
            </Box>
          );
        })()}
      </Box>
    </StatCard>
  );
};

// Drawdown Card
const DrawdownCard = ({ data }) => {
  const { t } = useTranslation();
  if (!data) return null;
  const drawdownView = data.drawdown_view || {};
  const drawdownPct = drawdownView.expected_drawdown_pct || data.expected_drawdown_pct || '0%';
  const absDd = Number(drawdownView.raw_drawdown_pct ?? 0);
  const ddColor = DRAWDOWN_BAND_COLORS[drawdownView.band] || DRAWDOWN_BAND_COLORS.light;
  const ddLabelKey = {
    very_deep: 'marketCorrection.ddCard.veryDeep',
    quite_deep: 'marketCorrection.ddCard.quiteDeep',
    moderate: 'marketCorrection.ddCard.moderate',
    light: 'marketCorrection.ddCard.light',
  }[drawdownView.band] || 'marketCorrection.ddCard.light';
  const ddLabel = t(ddLabelKey);

  const SupportRow = ({ label, value, highlight, color: rowColor, gap }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.35, borderRadius: 1, bgcolor: highlight ? `${rowColor || '#e65100'}08` : 'transparent' }}>
      <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: rowColor || 'text.secondary' }}>{typeof value === 'number' ? value.toLocaleString('vi-VN') : value}</Typography>
        {gap && <Typography sx={{ fontSize: '0.5rem', color: Number(gap) < 0 ? '#d32f2f' : 'text.disabled' }}>({gap}%)</Typography>}
      </Box>
    </Box>
  );

  return (
    <StatCard accentColor={ddColor}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {t('marketCorrection.ddCard.title')}
        </Typography>
        <Tooltip title={t('marketCorrection.ddCard.tooltip')} arrow placement="top">
          <InfoIcon sx={{ fontSize: '0.8rem', color: 'text.disabled', cursor: 'help' }} />
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.3 }}>
        <Typography sx={{ fontSize: '1.8rem', fontWeight: 900, color: ddColor, lineHeight: 1 }}>{drawdownPct}</Typography>
        <Chip label={ddLabel} size="small" sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: `${ddColor}12`, color: ddColor, '& .MuiChip-label': { px: 0.5 } }} />
      </Box>
      {/* Drawdown stats */}
      <Box sx={{ display: 'flex', gap: 1.5, mt: 0.6, mb: 0.5 }}>
        <Box>
          <Typography sx={{ fontSize: '0.5rem', color: 'text.disabled', fontWeight: 600 }}>{t('marketCorrection.ddCard.fromPeak20d')}</Typography>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: Math.abs(Number(drawdownView.dd20_pct || 0)) > 3 ? '#d32f2f' : 'text.secondary' }}>{Number(drawdownView.dd20_pct || 0).toFixed(2)}%</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.5rem', color: 'text.disabled', fontWeight: 600 }}>{t('marketCorrection.ddCard.expectedTotal')}</Typography>
          <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: Math.abs(Number(drawdownView.expected_total_dd_pct || 0)) > 5 ? '#d32f2f' : 'text.secondary' }}>{Number(drawdownView.expected_total_dd_pct || 0).toFixed(2)}%</Typography>
        </Box>
      </Box>

      {/* DD visual gauge */}
      <Box sx={{ mb: 0.8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2 }}>
          <Typography sx={{ fontSize: '0.45rem', color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase' }}>{t('marketCorrection.ddCard.ddLevel')}</Typography>
          <Typography sx={{ fontSize: '0.45rem', color: 'text.disabled', fontWeight: 600 }}>{absDd.toFixed(1)}% / 10%</Typography>
        </Box>
        <Box sx={{ height: 6, borderRadius: 3, bgcolor: '#f0f0f0', overflow: 'hidden', position: 'relative' }}>
          <Box sx={{ width: `${Math.min(absDd / 10 * 100, 100)}%`, height: '100%', borderRadius: 3, bgcolor: ddColor, transition: 'width 0.5s' }} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.15 }}>
          <Typography sx={{ fontSize: '0.4rem', color: '#388e3c', fontWeight: 600 }}>{t('marketCorrection.ddCard.gaugeLight')}</Typography>
          <Typography sx={{ fontSize: '0.4rem', color: '#f57c00', fontWeight: 600 }}>{t('marketCorrection.ddCard.gaugeMedium')}</Typography>
          <Typography sx={{ fontSize: '0.4rem', color: '#d32f2f', fontWeight: 600 }}>{t('marketCorrection.ddCard.gaugeDeep')}</Typography>
        </Box>
      </Box>

      {/* Support levels + ML context */}
      <Box sx={{ pt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.15 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.15 }}>
          <Typography sx={{ fontSize: '0.5rem', color: 'text.disabled', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t('marketCorrection.ddCard.support')} {drawdownView.close_price ? `(${t('marketCorrection.ddCard.price')}: ${Number(drawdownView.close_price).toLocaleString('vi-VN')})` : ''}</Typography>
          {drawdownView.ml_elevated && <Chip label={`ML: ${Number(drawdownView.composite_score || 0).toFixed(0)}/100`} size="small" sx={{ height: 14, fontSize: '0.45rem', fontWeight: 700, bgcolor: '#fff3e0', color: '#e65100', '& .MuiChip-label': { px: 0.3 } }} />}
        </Box>
        {(drawdownView.supports || []).map((row) => {
          const rowLabel = row.code === 'may_drop_to'
            ? t('marketCorrection.ddCard.mayDropTo')
            : row.code === 'sma20'
            ? 'MA20'
            : row.code === 'sma50'
            ? 'MA50'
            : 'Low 10d';
          const rowColor = row.tone === 'warning' ? '#e65100' : row.tone === 'danger' ? '#d32f2f' : 'text.secondary';
          const rowValue = row.code === 'may_drop_to' ? `~${Number(row.value).toLocaleString('vi-VN')}` : row.value;
          return <SupportRow key={row.code} label={rowLabel} value={rowValue} highlight={row.highlight} color={rowColor} gap={row.gap_pct != null ? Number(row.gap_pct).toFixed(1) : null} />;
        })}
      </Box>
    </StatCard>
  );
};

// History Chart Component
const HistoryChart = ({ data, mlData, currentData }) => {
  const { t } = useTranslation();
  if (!data || data.length === 0) {
    return (
      <Card sx={{ height: 400 }}>
        <CardContent>
          <Typography variant="h6">{t('marketCorrection.history.noData')}</Typography>
        </CardContent>
      </Card>
    );
  }

  // Compute composite score for reference line
  const ruleScore = currentData?.risk_score || 0;
  const summaryView = currentData?.summary_view || {};
  const composite = Number(summaryView.score ?? ruleScore);
  const source = summaryView.source || 'rule';
  const showMlLine = source === 'ml' && composite > ruleScore + 5;

  // Prepare data for recharts (reverse to show oldest first)
  const chartData = [...data].reverse().map(d => ({
    date: new Date(d.date).toLocaleDateString('vi-VN', { month: 'short', day: 'numeric' }),
    fullDate: d.date,
    risk_score: d.risk_score,
    regime: d.regime,
    severity: d.severity,
  }));

  return (
    <Card sx={{ height: 400 }}>
      <CardContent sx={{ height: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">
            {t('marketCorrection.history.title')}
          </Typography>
          {showMlLine && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 16, height: 2, bgcolor: '#1565c0', borderRadius: 1 }} />
              <Typography sx={{ fontSize: '0.6rem', color: '#1565c0', fontWeight: 600 }}>ML Composite: {composite.toFixed(0)}</Typography>
            </Box>
          )}
        </Box>
        <ResponsiveContainer width="100%" height="85%">
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorRisk" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f44336" stopOpacity={0.8}/>
                <stop offset="95%" stopColor="#f44336" stopOpacity={0.1}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12 }}
            />
            <YAxis 
              domain={[0, 100]}
              tick={{ fontSize: 12 }}
            />
            <RechartsTooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  
                  // Helper function to translate regime
                  const getRegimeLabel = (regime) => {
                    switch (regime?.toLowerCase()) {
                      case 'risk-on': return t('marketCorrection.regime.riskOn');
                      case 'caution': return t('marketCorrection.regime.caution');
                      case 'risk-off': return t('marketCorrection.regime.riskOff');
                      default: return regime;
                    }
                  };
                  
                  return (
                    <Paper sx={{ p: 1.5 }}>
                      <Typography variant="caption" display="block">
                        {new Date(data.fullDate).toLocaleDateString('vi-VN')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {t('marketCorrection.history.risk')}: {data.risk_score?.toFixed(1) || 'N/A'}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {t('marketCorrection.history.regime')}: {getRegimeLabel(data.regime)}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {t('marketCorrection.history.severity')}: {data.severity}
                      </Typography>
                    </Paper>
                  );
                }
                return null;
              }}
            />
            <Area 
              type="monotone" 
              dataKey="risk_score" 
              stroke="#f44336" 
              strokeWidth={2}
              fill="url(#colorRisk)" 
            />
            {showMlLine && (
              <ReferenceLine y={composite} stroke="#1565c0" strokeDasharray="6 3" strokeWidth={1.5}
                label={{ value: `ML ${composite.toFixed(0)}`, position: 'right', fill: '#1565c0', fontSize: 11, fontWeight: 600 }} />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

// Support Levels Card Component
const SupportLevelsCard = ({ data }) => {
  const { t } = useTranslation();
  if (!data) return null;

  const levels = data.support_levels || {};

  return (
    <Card sx={{ height: 400 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('marketCorrection.supportLevels.title')}
        </Typography>
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('marketCorrection.supportLevels.sma20')}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {levels.sma20?.toLocaleString('vi-VN') || 'N/A'}
          </Typography>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('marketCorrection.supportLevels.sma50')}
          </Typography>
          <Typography variant="h5" fontWeight="bold">
            {levels.sma50?.toLocaleString('vi-VN') || 'N/A'}
          </Typography>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('marketCorrection.supportLevels.low10')}
          </Typography>
          <Typography variant="h5" fontWeight="bold" color="error.main">
            {levels.low10?.toLocaleString('vi-VN') || 'N/A'}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="caption">
            {t('marketCorrection.supportLevels.info')}
          </Typography>
        </Alert>
      </CardContent>
    </Card>
  );
};

// Factors Breakdown Card Component - Compact Grid Design
const FactorsBreakdownCard = ({ data }) => {
  const { t } = useTranslation();

  if (!data) return null;
  const factorsView = data.factors_view || {};
  const source = factorsView.source || 'rule';
  const composite = Number(factorsView.composite_score ?? data.risk_score ?? 0);
  const mlMax = Number(factorsView.ml_max_pct ?? 0);

  const factors = [
    { key: 'f1_trend_break', name: t('marketCorrection.factors.f1.name'), weight: 0.20,
      desc: t('marketCorrection.factorDesc.f1'), levels: t('marketCorrection.factorLevels.f1', { returnObjects: true }) },
    { key: 'f2_slope_decay', name: t('marketCorrection.factors.f2.name'), weight: 0.10,
      desc: t('marketCorrection.factorDesc.f2'), levels: t('marketCorrection.factorLevels.f2', { returnObjects: true }) },
    { key: 'f3_distribution', name: t('marketCorrection.factors.f3.name'), weight: 0.15,
      desc: t('marketCorrection.factorDesc.f3'), levels: t('marketCorrection.factorLevels.f3', { returnObjects: true }) },
    { key: 'f4_panic', name: t('marketCorrection.factors.f4.name'), weight: 0.15,
      desc: t('marketCorrection.factorDesc.f4'), levels: t('marketCorrection.factorLevels.f4', { returnObjects: true }) },
    { key: 'f5_exhaustion', name: t('marketCorrection.factors.f5.name'), weight: 0.10,
      desc: t('marketCorrection.factorDesc.f5'), levels: t('marketCorrection.factorLevels.f5', { returnObjects: true }) },
    { key: 'f6_vol_expand', name: t('marketCorrection.factors.f6.name'), weight: 0.10,
      desc: t('marketCorrection.factorDesc.f6'), levels: t('marketCorrection.factorLevels.f6', { returnObjects: true }) },
    { key: 'f7_breadth', name: t('marketCorrection.factors.f7.name'), weight: 0.10,
      desc: t('marketCorrection.factorDesc.f7'), levels: t('marketCorrection.factorLevels.f7', { returnObjects: true }) },
    { key: 'f8_gap20', name: t('marketCorrection.factors.f8.name'), weight: 0.10,
      desc: t('marketCorrection.factorDesc.f8'), levels: t('marketCorrection.factorLevels.f8', { returnObjects: true }) },
  ];

  const factorsWithValues = factors.map((factor) => {
    const row = (factorsView.rows || []).find((item) => item.key === factor.key) || {};
    const levelIndex = row.level_bucket === 'high' ? 2 : row.level_bucket === 'medium' ? 1 : 0;
    const styles = FACTOR_STATUS_STYLES[row.status] || FACTOR_STATUS_STYLES.safe;
    return {
      ...factor,
      value: row.value || 0,
      isActive: Boolean(row.is_active),
      contribution: Number(row.contribution || 0).toFixed(0),
      maxContribution: Number(row.max_contribution || factor.weight * 100).toFixed(0),
      status: t(`marketCorrection.factorStatus.${row.status || 'safe'}`),
      statusColor: styles.color,
      statusBg: styles.bg,
      levelDesc: factor.levels[levelIndex],
    };
  });
  const activeCount = Number(factorsView.active_count ?? 0);
  const totalContribution = Number(factorsView.total_contribution ?? 0).toFixed(0);
  const sorted = [...factorsWithValues];

  return (
    <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, width: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header matching MarketHealth style */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, borderBottom: '2px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.02)' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
            {t('marketCorrection.factors.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {source === 'ml' ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled', fontWeight: 500, textDecoration: 'line-through' }}>
                  {totalContribution}/100
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: composite > 50 ? '#d32f2f' : composite > 30 ? '#f57c00' : '#388e3c', fontWeight: 700 }}>
                  {composite.toFixed(0)}/100
                </Typography>
                <Chip label="ML" size="small" sx={{ height: 16, fontSize: '0.5rem', fontWeight: 700, bgcolor: '#1565c0', color: '#fff', '& .MuiChip-label': { px: 0.4 } }} />
              </Box>
            ) : (
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>
                {totalContribution}/100
              </Typography>
            )}
            <Chip label={`${activeCount}/8`} size="small"
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700,
                background: activeCount >= 5 ? '#d32f2f' : activeCount >= 3 ? '#f57c00' : '#388e3c', color: 'white' }}
            />
          </Box>
        </Box>

        {/* Column header */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid rgba(0,0,0,0.08)', bgcolor: 'rgba(0,0,0,0.015)' }}>
          {[t('marketCorrection.factorColumn.main'), t('marketCorrection.factorColumn.secondary')].map((title, ci) => (
            <Box key={ci} sx={{ px: 1.5, py: 0.5, borderLeft: ci > 0 ? '1px solid rgba(0,0,0,0.06)' : 'none' }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {title}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* 2-column factor list */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
          {/* Left column: first 4 (sorted) */}
          <Box sx={{ borderRight: '1px solid rgba(0,0,0,0.06)' }}>
            {sorted.slice(0, 4).map((f, i) => (
              <Box key={f.key} sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
                borderBottom: i < 3 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                opacity: f.isActive ? 1 : 0.5,
              }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: f.isActive ? f.statusColor : '#ccc', flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                    {f.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', lineHeight: 1.2 }}>
                    {f.levelDesc}
                  </Typography>
                </Box>
                <Chip label={f.status} size="small"
                  sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, flexShrink: 0,
                    bgcolor: f.isActive ? f.statusColor : '#e0e0e0', color: f.isActive ? '#fff' : '#999',
                    '& .MuiChip-label': { px: 0.6 } }}
                />
              </Box>
            ))}
          </Box>
          {/* Right column: last 4 (sorted) */}
          <Box>
            {sorted.slice(4, 8).map((f, i) => (
              <Box key={f.key} sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1,
                borderBottom: i < 3 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                opacity: f.isActive ? 1 : 0.5,
              }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: f.isActive ? f.statusColor : '#ccc', flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                    {f.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', lineHeight: 1.2 }}>
                    {f.levelDesc}
                  </Typography>
                </Box>
                <Chip label={f.status} size="small"
                  sx={{ height: 18, fontSize: '0.55rem', fontWeight: 700, flexShrink: 0,
                    bgcolor: f.isActive ? f.statusColor : '#e0e0e0', color: f.isActive ? '#fff' : '#999',
                    '& .MuiChip-label': { px: 0.6 } }}
                />
              </Box>
            ))}
          </Box>
        </Box>

        {/* ML divergence note — when rules say safe but ML detects risk */}
        {source === 'ml' && activeCount === 0 && (
          <Box sx={{ mt: 'auto', px: 1.5, py: 1, borderTop: '1px solid rgba(0,0,0,0.06)', bgcolor: '#fff3e0', display: 'flex', alignItems: 'flex-start', gap: 0.8 }}>
            <InfoIcon sx={{ fontSize: '0.85rem', color: '#f57c00', mt: 0.15, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.65rem', color: '#e65100', lineHeight: 1.4 }} dangerouslySetInnerHTML={{ __html: t('marketCorrection.mlDivergenceNote', { mlMax: Math.round(mlMax) }) }} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Market Health Card - TABLE/GRID FORMAT (Compact, Spreadsheet-style)
const MarketHealthCard = ({ data }) => {
  const { t } = useTranslation();
  if (!data || !data.health_view) return null;
  const healthView = data.health_view || {};
  const rows = (healthView.rows || []).map((row) => {
    let metric = '';
    let value = `${Number(row.value || 0).toFixed(row.code === 'drawdown_20d' ? 2 : 0)}%`;
    let components = '';
    if (row.code === 'oversold_score') {
      metric = t('marketCorrection.oversold.score');
      components = `${t('marketCorrection.oversold.os1')}:${Number(row.components?.os1 || 0).toFixed(0)}% ${t('marketCorrection.oversold.os2')}:${Number(row.components?.os2 || 0).toFixed(0)}% ${t('marketCorrection.oversold.os3')}:${Number(row.components?.os3 || 0).toFixed(0)}%`;
    } else if (row.code === 'rebound_probability') {
      metric = t('marketCorrection.oversold.rebound');
      components = t(`marketCorrection.oversold.${row.signal_strength || 'weak'}Signal`);
    } else {
      metric = t('marketCorrection.drawdownTracker.drawdown20d');
      components = `${t('marketCorrection.drawdownTracker.additional')}:${Number(row.components?.additional_dd || 0).toFixed(2)}% ${t('marketCorrection.drawdownTracker.total')}:${Number(row.components?.total_dd || 0).toFixed(2)}%`;
    }
    return {
      metric,
      value,
      status: {
        text: t(`marketCorrection.status${(row.status || 'low').charAt(0).toUpperCase()}${(row.status || 'low').slice(1)}`),
        color: HEALTH_STATUS_COLORS[row.status] || '#9e9e9e',
      },
      components,
      trendIcon: trendIconForKey(row.trend),
      trendColor: trendColorForKey(row.trend),
    };
  });

  return (
    <Card 
      sx={{ 
        width: '100%',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.08)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          px: 2, 
          py: 1.5,
          borderBottom: '2px solid rgba(0,0,0,0.08)',
          backgroundColor: 'rgba(0,0,0,0.02)',
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontSize: '0.95rem',
              fontWeight: 700,
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <ShowChartIcon sx={{ fontSize: '1.2rem' }} />
            {t('marketCorrection.marketHealth')}
          </Typography>
          {healthView.recovery_active && (
            <Chip 
              label={t('marketCorrection.recoveryActive')} 
              size="small"
              sx={{ 
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 600,
                backgroundColor: '#4caf50',
                color: '#fff',
              }}
            />
          )}
        </Box>

        {/* Table Header */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: '2fr 1fr 1.2fr 2.5fr 0.5fr',
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          backgroundColor: 'rgba(0,0,0,0.03)',
        }}>
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#666' }}>
            {t('marketCorrection.metric')}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#666', textAlign: 'right' }}>
            {t('marketCorrection.value')}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#666', textAlign: 'center' }}>
            {t('marketCorrection.status')}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#666' }}>
            {t('marketCorrection.components')}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: '#666', textAlign: 'center' }}>
            {t('marketCorrection.trend')}
          </Typography>
        </Box>

        {/* Table Rows */}
        {rows.map((row, idx) => (
          <Box 
            key={idx}
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: '2fr 1fr 1.2fr 2.5fr 0.5fr',
              gap: 1,
              px: 2,
              py: 1.25,
              borderBottom: idx < rows.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
              transition: 'background-color 0.2s',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.02)',
              }
            }}
          >
            {/* Metric */}
            <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
              {row.metric}
            </Typography>

            {/* Value */}
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.95rem', 
                fontWeight: 800,
                textAlign: 'right',
                fontFamily: 'monospace',
              }}
            >
              {row.value}
            </Typography>

            {/* Status Text */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Typography
                variant="body2"
                sx={{ 
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: row.status.color,
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {row.status.text}
              </Typography>
            </Box>

            {/* Components */}
            <Typography 
              variant="caption" 
              sx={{ 
                fontSize: '0.7rem', 
                color: '#666',
                fontFamily: 'monospace',
              }}
            >
              {row.components}
            </Typography>

            {/* Trend */}
            <Box 
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: row.trendColor,
              }}
            >
              {React.cloneElement(row.trendIcon, { sx: { fontSize: '1.2rem' } })}
            </Box>
          </Box>
        ))}

        {/* ML elevated risk note — pushed to bottom */}
        {healthView.ml_elevated && (
          <Box sx={{ mt: 'auto', px: 2, py: 1, borderTop: '1px solid rgba(0,0,0,0.06)', bgcolor: '#fff8e1', display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <WarningIcon sx={{ fontSize: '0.8rem', color: '#f57c00', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.65rem', color: '#e65100', lineHeight: 1.3 }} dangerouslySetInnerHTML={{ __html: t('marketCorrection.mlHealthNote', { composite: Number(healthView.composite_score || 0).toFixed(0) }) }} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Actions Card Component
// Entry Signal Card Component
const EntrySignalCard = ({ signal }) => {
  const { t } = useTranslation();
  if (!signal || !signal.signal) return null;

  const data = signal.signal;
  const mkt = data.data || {};
  const signalView = data.signal_view || {};
  
  const getSignalConfig = (configKey) => {
    switch (configKey) {
      case 'probe':
        return {
          icon: <SearchIcon sx={{ fontSize: '2rem' }} />,
          color: '#2196f3',
          bg: 'linear-gradient(135deg, #2196f3 0%, #42a5f5 100%)',
          label: t('marketCorrection.entrySignal.probe', { defaultValue: 'PROBE - Thăm dò' }),
        };
      case 'confirm':
        return {
          icon: <VerifiedUserIcon sx={{ fontSize: '2rem' }} />,
          color: '#4caf50',
          bg: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
          label: t('marketCorrection.entrySignal.confirm', { defaultValue: 'CONFIRM - Xác nhận đáy' }),
        };
      case 'hold':
        return {
          icon: <TrendingUpIcon sx={{ fontSize: '2rem' }} />,
          color: '#673ab7',
          bg: 'linear-gradient(135deg, #673ab7 0%, #7e57c2 100%)',
          label: t('marketCorrection.entrySignal.hold', { defaultValue: 'HOLD - Theo xu hướng' }),
        };
      case 'add':
        return {
          icon: <AddCircleIcon sx={{ fontSize: '2rem' }} />,
          color: '#9c27b0',
          bg: 'linear-gradient(135deg, #9c27b0 0%, #ab47bc 100%)',
          label: t('marketCorrection.entrySignal.add', { defaultValue: 'ADD - Thêm vị thế' }),
        };
      case 'wait_healthy':
        return {
          icon: <InfoIcon sx={{ fontSize: '2rem' }} />,
          color: '#546e7a',
          bg: 'linear-gradient(135deg, #546e7a 0%, #78909c 100%)',
          label: t('marketCorrection.entrySignal.wait', { defaultValue: 'Thị trường ổn định — Không cần hành động' }),
        };
      case 'wait_caution':
        return {
          icon: <WaitIcon sx={{ fontSize: '2rem' }} />,
          color: '#f57c00',
          bg: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)',
          label: t('marketCorrection.entryDetail.waitMedRisk'),
        };
      default:
        return {
          icon: <WaitIcon sx={{ fontSize: '2rem' }} />,
          color: '#ff9800',
          bg: 'linear-gradient(135deg, #ff9800 0%, #ffa726 100%)',
          label: t('marketCorrection.entrySignal.wait', { defaultValue: 'WAIT - Chờ tín hiệu' }),
        };
    }
  };

  const config = getSignalConfig(signalView.config_key || data.signal_type?.toLowerCase());

  const rs = Number(signalView.risk_score ?? mkt.risk_score ?? 0);
  const os = Number(signalView.context_metric?.value_pct ?? (mkt.oversold || 0) * 100);
  const close = mkt.close || 0;
  const gapPct = Number(signalView.gap_pct ?? 0);

  // Parse reasons/warnings
  const parseReason = (reason) => {
    const parts = reason.split(':');
    const key = parts[0];
    const vals = parts.slice(1);
    let txt = t(`marketCorrection.entrySignal.reasonKeys.${key}`, { defaultValue: reason });
    if (vals.length > 0) {
      txt = txt.replace('{value}', vals[0]).replace('{current}', vals[0]).replace('{previous}', vals[1] || '').replace('{close}', vals[0]).replace('{high3d}', vals[1] || '').replace('{closePos}', vals[0]).replace('{volIncrease}', vals[1] || '').replace('{prevHigh}', vals[1] || '');
    }
    return txt;
  };
  const parseWarning = (warning) => {
    const [wKey, wVal] = warning.includes(':') ? warning.split(':', 2) : [warning, null];
    let txt = t(`marketCorrection.entrySignal.warningKeys.${wKey}`, { defaultValue: warning });
    if (wVal) { txt = txt.replace('{value}', wVal); if (wKey.includes('STOP_LOSS')) txt = `${t('marketCorrection.entrySignal.stopLossInfo')}: ${wVal}`; }
    return txt;
  };

  const riskColor = RISK_BAND_COLORS[signalView.risk_band] || RISK_BAND_COLORS.safe;
  const riskLabel = t({ very_dangerous: 'marketCorrection.entryDetail.riskHigh', dangerous: 'marketCorrection.entryDetail.riskDangerous', caution: 'marketCorrection.entryDetail.riskWatch', safe: 'marketCorrection.entryDetail.riskLow' }[signalView.risk_band] || 'marketCorrection.entryDetail.riskLow');
  const osColor = os > 70 ? '#1565c0' : os > 40 ? '#42a5f5' : '#9e9e9e';
  const osTitle = t(signalView.context_metric?.title_key || 'marketCorrection.entryDetail.oversold');
  const osLabel = t(signalView.context_metric?.label_key || 'marketCorrection.entryDetail.oversoldNone');
  const gapColor = gapPct >= 0 ? '#388e3c' : '#d32f2f';

  return (
    <Card sx={{ borderRadius: 2.5, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden', border: 'none' }}>
      {/* Hero header with signal */}
      <Box sx={{ background: config.bg, px: 2.5, py: 2, position: 'relative' }}>
        <Box sx={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', opacity: 0.08 }}>
          {React.cloneElement(config.icon, { sx: { fontSize: '5rem', color: 'white' } })}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
          <Box>
            <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', mb: 0.3 }}>
              {t('marketCorrection.entryDetail.signalLabel')}
            </Typography>
            <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color: 'white', lineHeight: 1.1, letterSpacing: '-0.02em' }}>
              {config.label}
            </Typography>
          </Box>
          {data.signal_type !== 'WAIT' && data.confidence > 0 && (
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{t('marketCorrection.entryDetail.confidence')}</Typography>
              <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: 'white', lineHeight: 1 }}>{(data.confidence * 100).toFixed(0)}%</Typography>
              {data.signal_type !== 'WAIT' && <Chip label={data.position_size} size="small"
                sx={{ mt: 0.5, height: 20, fontSize: '0.6rem', fontWeight: 700, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '& .MuiChip-label': { px: 0.6 } }} />}
            </Box>
          )}
        </Box>
      </Box>

      {/* Metric strip */}
      <Box sx={{ display: 'flex', bgcolor: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        {/* Risk gauge */}
        <Box sx={{ flex: 1, p: 1.5, textAlign: 'center', borderRight: '1px solid rgba(0,0,0,0.06)' }}>
          <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('marketCorrection.entryDetail.risk')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.3, mt: 0.3 }}>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 900, color: riskColor, lineHeight: 1 }}>{rs.toFixed(0)}</Typography>
            <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>/100</Typography>
          </Box>
          <Box sx={{ mt: 0.5, mx: 'auto', width: '80%', height: 4, borderRadius: 2, bgcolor: '#eee', overflow: 'hidden' }}>
            <Box sx={{ width: `${Math.min(rs, 100)}%`, height: '100%', borderRadius: 2, bgcolor: riskColor, transition: 'width 0.5s' }} />
          </Box>
          <Typography sx={{ fontSize: '0.55rem', color: riskColor, fontWeight: 700, mt: 0.3 }}>{riskLabel}</Typography>
        </Box>

        {/* Mean-reversion context */}
        <Box sx={{ flex: 1, p: 1.5, textAlign: 'center', borderRight: '1px solid rgba(0,0,0,0.06)' }}>
          <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{osTitle}</Typography>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 900, color: osColor, lineHeight: 1, mt: 0.3 }}>{os.toFixed(0)}%</Typography>
          <Box sx={{ mt: 0.5, mx: 'auto', width: '80%', height: 4, borderRadius: 2, bgcolor: '#eee', overflow: 'hidden' }}>
            <Box sx={{ width: `${Math.min(os, 100)}%`, height: '100%', borderRadius: 2, bgcolor: osColor, transition: 'width 0.5s' }} />
          </Box>
          <Typography sx={{ fontSize: '0.55rem', color: osColor, fontWeight: 700, mt: 0.3 }}>{osLabel}</Typography>
        </Box>

        {/* Price & Gap */}
        <Box sx={{ flex: 1, p: 1.5, textAlign: 'center', borderRight: data.stop_loss ? '1px solid rgba(0,0,0,0.06)' : 'none' }}>
          <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>VNINDEX</Typography>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 900, color: '#1a237e', lineHeight: 1, mt: 0.3 }}>
            {close.toLocaleString('vi-VN', { maximumFractionDigits: 0 })}
          </Typography>
          <Chip label={`${gapPct >= 0 ? '+' : ''}${gapPct.toFixed(1)}% vs MA20`} size="small"
            sx={{ mt: 0.5, height: 18, fontSize: '0.55rem', fontWeight: 700, bgcolor: `${gapColor}12`, color: gapColor, '& .MuiChip-label': { px: 0.5 } }} />
        </Box>

        {/* Stop Loss */}
        {data.stop_loss && (
          <Box sx={{ flex: 1, p: 1.5, textAlign: 'center', bgcolor: '#fff5f5' }}>
            <Typography sx={{ fontSize: '0.55rem', color: '#d32f2f', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('marketCorrection.entryDetail.stopLoss')}</Typography>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 900, color: '#d32f2f', lineHeight: 1, mt: 0.3 }}>
              {data.stop_loss.toFixed(0)}
            </Typography>
            <Typography sx={{ fontSize: '0.55rem', color: '#d32f2f', mt: 0.4, opacity: 0.7 }}>{t('marketCorrection.entryDetail.stopLossNote')}</Typography>
          </Box>
        )}
      </Box>

      {/* Reasons & Warnings */}
      {(data.reason?.length > 0 || data.warnings?.length > 0) && (
        <Box sx={{ px: 2, py: 1.2, display: 'flex', gap: 2 }}>
          {data.reason?.length > 0 && (
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: config.color, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CheckCircleIcon sx={{ fontSize: '0.7rem' }} /> {t('marketCorrection.entryDetail.reasons')}
              </Typography>
              {data.reason.map((r, i) => (
                <Typography key={i} sx={{ fontSize: '0.7rem', color: 'text.primary', lineHeight: 1.5, pl: 1.5, position: 'relative', '&::before': { content: '"•"', position: 'absolute', left: 4, color: config.color } }}>
                  {parseReason(r)}
                </Typography>
              ))}
            </Box>
          )}
          {data.warnings?.length > 0 && (
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#ef6c00', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningIcon sx={{ fontSize: '0.7rem' }} /> {t('marketCorrection.entryDetail.warnings')}
              </Typography>
              {data.warnings.map((w, i) => (
                <Typography key={i} sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.5, pl: 1.5, position: 'relative', '&::before': { content: '"•"', position: 'absolute', left: 4, color: '#ef6c00' } }}>
                  {parseWarning(w)}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      )}
      {(!data.reason?.length && !data.warnings?.length) && (() => {
        // Dynamic message based on COMPOSITE risk (includes ML)
        const waitMsg = t(signalView.wait_message_key || 'marketCorrection.entrySignal.waitPending', signalView.wait_message_params || {});
        const isHealthyMarket = signalView.config_key === 'wait_healthy';
        const isCaution = signalView.config_key === 'wait_caution';
        const waitColor = isHealthyMarket ? '#546e7a' : isCaution ? '#f57c00' : '#f57f17';
        const waitBg = isHealthyMarket ? '#eceff1' : isCaution ? '#fff3e0' : '#fffde7';
        const WaitStatusIcon = isHealthyMarket ? InfoIcon : WaitIcon;
        return (
          <Box sx={{ px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: waitBg }}>
            <WaitStatusIcon sx={{ fontSize: '0.9rem', color: waitColor }} />
            <Typography sx={{ fontSize: '0.72rem', color: waitColor, fontWeight: 500 }}>
              {waitMsg}
            </Typography>
          </Box>
        );
      })()}
    </Card>
  );
};

const ActionsCard = ({ data }) => {
  const { t, i18n } = useTranslation();
  if (!data) return null;

  const marketView = data.market_view || {};
  const regime = (marketView.regime || data.regime || 'Unknown').trim();
  const mlWarning = Boolean(marketView.ml_warning);
  const mlWarningScore = marketView.ml_score ?? null;

  const isRiskOff = regime === 'Risk-off';
  const isCaution = regime === 'Caution';
  const severity = isRiskOff ? 'error' : isCaution ? 'warning' : 'success';
  const actionIcon = isRiskOff ? <TrendingDownIcon /> : isCaution ? <WarningIcon /> : <CheckCircleIcon />;
  const actionLabel = isRiskOff
    ? t('marketCorrection.actions.riskOff', { defaultValue: 'Phòng thủ' })
    : isCaution
    ? t('marketCorrection.actions.caution', { defaultValue: 'Thận trọng' })
    : t('marketCorrection.actions.riskOn', { defaultValue: 'Thuận lợi' });

  const lang = i18n.language?.startsWith('vi') ? 'vi' : 'en';
  const actionsText = marketView.actions?.[lang] || marketView.actions?.vi || data.actions?.[lang] || data.actions?.vi || '';

  return (
    <Alert severity={severity} icon={actionIcon}
      sx={{ borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
            {t('marketCorrection.actions.label', { defaultValue: 'Khuyến nghị' })}: {actionLabel}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', mt: 0.3 }}>
            {actionsText}
          </Typography>
            {mlWarning && (
              <Typography sx={{ fontSize: '0.64rem', mt: 0.45, color: '#b45309', fontWeight: 600 }}>
                {t('marketCorrection.actions.mlWarningInline', {
                  defaultValue: 'AI đang cảnh báo rủi ro {{score}}/100, nên ưu tiên quản trị vị thế hơn là mở mới mạnh tay.',
                  score: Number(mlWarningScore || 0).toFixed(0),
                })}
              </Typography>
            )}
        </Box>
        <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', whiteSpace: 'nowrap', ml: 2 }}>
          {new Date(data.date).toLocaleDateString(lang === 'vi' ? 'vi-VN' : 'en-US')}
        </Typography>
      </Box>
    </Alert>
  );
};

export default MarketCorrectionPage;
